import { type ConfigDetailsState } from '@repo/config-services';
import {
  ALL_DATA_TYPES, type BaseOperatorType, type BaseOptionsType, type DataTypeType,
  DEAL_CONDITION_KEY,
  FILTERABLE_ENTITY_TYPES,
  LEAD_ACTIVITY_CONDITION_KEY,
  SALES_GROUP_CONDITION_KEY,
  TASK_TYPE_CONDITION_KEY,
} from 'advanced-filters/data';
import {
  CustomConditionKeyConfig,
  type CustomConditionKeys,
  customConditionKeys,
} from 'advanced-filters/data/customConditionKeys';
import { DEAL_OPTIONS } from 'advanced-filters/data/dealOptions';
import { TASK_TYPE_OPTIONS } from 'advanced-filters/data/taskOptions';
import { type GetLeadAttributesType } from 'advanced-filters/queries';
import { type FilterForm } from 'advanced-filters/store';
import type { CriteriaItem, FilterLevel } from 'advanced-filters/types/filter';

export const getAttributeBySchemaName = (
  attributes: any[],
  schemaName: string,
) => attributes.find((attribute: any) => attribute.schemaName === schemaName);

export const isSchemaUserType = (schemaName: string) => (
  schemaName === 'OwnerId'
  || schemaName === 'UpdatedById'
  || schemaName === 'CreatedById'
  || schemaName === 'Owner'
  || schemaName === 'owner'
);

export const transformOutgoingFilterData = (
  condition: any = [],
) => condition.reduce((acc: any[], item: FilterForm | CriteriaItem) => {
  if (!item) return acc; // Skip undefined or null items

  // If it's a bucket (level), transform its criteria
  if ('type' in item && item.type === 'level') {
    acc.push({
      type: 'level',
      level: item.level,
      operator: item.operator.toUpperCase(), // Ensure operator is uppercase
      criteria: transformOutgoingFilterData(item.criteria),
    });
    return acc;
  }

  // Handle filter items
  let conditionValue = item?.conditionValue?.value;

  // Check if the value is an array of objects containing 'value' and 'label'
  if (Array.isArray(conditionValue)) {
    conditionValue = conditionValue.map((element: any) => element.value);
  }

  // Check if the value is an object for date subfilter with a 'value' key containing { filterType, from, to }
  if (
    typeof conditionValue === 'object'
    && !Array.isArray(conditionValue)
    && conditionValue?.value
    && typeof conditionValue.value === 'object'
  ) {
    conditionValue = conditionValue.value;
  }

  let subConditionValue = item.subConditionValue?.value;
  if (Array.isArray(subConditionValue)) {
    subConditionValue = subConditionValue.map((element: any) => element.value);
  }

  // Apply the same logic to subConditionValue for date filter
  if (
    typeof subConditionValue === 'object'
    && !Array.isArray(subConditionValue)
    && subConditionValue?.value
    && typeof subConditionValue.value === 'object'
  ) {
    subConditionValue = subConditionValue.value;
  }

  // Convert conditionKey object to string format
  let conditionKey = '';
  if (item.conditionKey && typeof item.conditionKey === 'object') {
    if ('conditionKeyPrefix' in item.conditionKey) {
      conditionKey = `${item.conditionKey.conditionKeyPrefix}.${item.conditionKey.schemaName}`;
    } else {
      conditionKey = `${item.conditionKey.entityType}.${item.conditionKey.schemaName}`;
    }
  } else if (typeof item.conditionKey === 'string') {
    conditionKey = item.conditionKey; // Already in string format
  }

  const subConditionKey = item.subConditionKey?.key;

  acc.push({
    conditionComparator: item.conditionComparator,
    conditionValue: {
      value: conditionValue,
    },
    conditionKey,
    subConditionKey,
    subConditionComparator: subConditionKey && item.subConditionComparator,
    subConditionValue: subConditionKey && {
      value: subConditionValue,
    },
    timeFilter: item.timeFilter,
  });

  return acc;
}, []);

export const transformIncomingFilterData = (data: any, attributes: GetLeadAttributesType['leadAttributes']) => {
  // Initialize sets to track referenced data
  const referencedLeadAttributes = new Set<string>();
  const referencedLeadAttributeOptions = new Set<number>();
  const referencedUsers = new Set<string>();
  const referencedActivities = new Set<number>();
  const referencedSalesGroups = new Set<number>();
  const referencedTasks = new Set<number>();
  const referencedProducts = new Set<number>();
  const referencedDealStatuses = new Set<number>();

  // Handle both single object and array responses
  const criteriaGroup = data;

  // Function to generate a UUID-like string for IDs
  const generateId = () => crypto.randomUUID();

  // Process each criteria group and build a hierarchical structure
  const transformedBuckets: any[] = [];

  const comparator = criteriaGroup?.criteriaLevels?.[0]?.operator?.toUpperCase();

  criteriaGroup.criteriaLevels.forEach((criteriaLevel: any) => {
    const { criterias } = criteriaLevel;

    // Create maps to store criteria by ID and children by parent ID
    const criteriaMap = new Map<string | number, any>();
    const childrenMap = new Map<string | number, any[]>();

    // First pass: transform all criteria and build the maps
    criterias.forEach((criteria: any) => {
      // Store in criteria map - ensure IDs are strings
      const criteriaId = String(criteria.id);
      criteriaMap.set(criteriaId, criteria);

      // Add to children map if it has a parent - ensure parent IDs are strings
      if (criteria.parentId) {
        const parentId = String(criteria.parentId);
        if (!childrenMap.has(parentId)) {
          childrenMap.set(parentId, []);
        }
        const children = childrenMap.get(parentId);
        if (children) {
          children.push(criteria);
        }
      }
    });

    // Find root nodes (those with no parent or with a parent that doesn't exist)
    const rootNodes = criterias.filter((criteria: any) => (
      !criteria.parentId || !criteriaMap.has(String(criteria.parentId))
    ));

    // Function to transform a criteria node
    const transformCriteria = (criteria: any) => {
      // Skip criteria without conditionKey (parent nodes)
      if (!criteria.conditionKey) {
        return null;
      }

      // Create a transformed criteria object based on the configuration map
      const [prefixConditionKey, schemaName] = criteria.conditionKey.split('.');
      const config = getAttributeBySchemaName(attributes, schemaName);

      let entityType = prefixConditionKey;
      if (customConditionKeys.includes(prefixConditionKey)) {
        entityType = CustomConditionKeyConfig[prefixConditionKey as CustomConditionKeys].entityType;
      }

      let conditionKey: BaseOptionsType = {
        value: config?.value,
        label: config?.attributeName,
        dataType: config?.dataType,
        schemaName,
        entityType,
        conditionKeyPrefix: prefixConditionKey,
      };

      const {
        conditionValue,
        conditionTimeFilter,
        subConditionValue,
        timeFilter,
        subConditionTimeFilter,
      } = criteria;
      const transformedConditionValue = { value: conditionValue?.value };

      const subConditionKey = criteria.subConditionKey ? {
        id: '',
        value: '',
        label: criteria.subConditionKey,
        key: criteria.subConditionKey,
        dataType: 'default',
        entityType,
      } : undefined;

      if (Array.isArray(conditionValue?.value)) {
        transformedConditionValue.value = conditionValue.value.map((val: string) => ({ value: val }));
      }

      if (conditionTimeFilter) {
        transformedConditionValue.value = {
          timeFilter: {
            filterType: conditionTimeFilter.filterName,
            fromTime: conditionTimeFilter.fromTime,
            toTime: conditionTimeFilter.toTime,
            offsetValue: conditionTimeFilter.offsetValue,
          },
        };
      }

      const transformedSubConditionValue = { value: subConditionValue?.value };
      if (Array.isArray(subConditionValue?.value)) {
        transformedSubConditionValue.value = subConditionValue.value.map((val: string) => ({ value: val }));
      }
      if (subConditionTimeFilter) {
        transformedSubConditionValue.value = {
          timeFilter: {
            filterType: subConditionTimeFilter?.filterName,
            fromTime: subConditionTimeFilter?.fromTime,
            toTime: subConditionTimeFilter?.toTime,
            offsetValue: subConditionTimeFilter?.offsetValue,
          },
        };
      }

      if (entityType === FILTERABLE_ENTITY_TYPES.leadAttribute) {
        referencedLeadAttributes.add(schemaName);
        if (isSchemaUserType(schemaName)) {
          (conditionValue.value as string[]).forEach((user) => referencedUsers.add(user));
        }
        if (conditionValue?.value && Array.isArray(conditionValue?.value)) {
          (conditionValue.value as string[]).forEach((option) => referencedLeadAttributeOptions.add(+option));
        }
      }

      if (entityType === FILTERABLE_ENTITY_TYPES.leadActivity) {
        // Set condition key to lead activity condition key
        conditionKey = LEAD_ACTIVITY_CONDITION_KEY;

        const activityId = transformedConditionValue.value?.[0]?.value;
        if (activityId && activityId !== 'any') {
          referencedActivities.add(+activityId);
        }
        if (isSchemaUserType(criteria.subConditionKey)) {
          (subConditionValue.value as string[]).forEach((user) => referencedUsers.add(user));
        }
      }

      if (entityType === FILTERABLE_ENTITY_TYPES.salesGroup) {
        conditionKey = SALES_GROUP_CONDITION_KEY;
        if (conditionValue && Array.isArray(conditionValue?.value)) {
          (conditionValue.value as number[]).forEach((salesGroupId) => referencedSalesGroups.add(+salesGroupId));
        }
      }

      if (entityType === FILTERABLE_ENTITY_TYPES.taskType) {
        const taskId = transformedConditionValue.value?.[0]?.value;
        if (taskId && taskId !== 'any') {
          referencedTasks.add(+taskId);
        }
        conditionKey = TASK_TYPE_CONDITION_KEY;

        if (isSchemaUserType(criteria.subConditionKey)) {
          (subConditionValue.value as string[]).forEach((user) => referencedUsers.add(user));
        }

        if (subConditionKey) {
          const taskSubConditionKey = TASK_TYPE_OPTIONS.find((option) => option.key === subConditionKey.key);
          if (taskSubConditionKey) {
            subConditionKey.label = taskSubConditionKey.value;
            subConditionKey.dataType = taskSubConditionKey.dataType;

            if ('children' in taskSubConditionKey && Array.isArray(subConditionValue?.value)) {
              transformedSubConditionValue.value = subConditionValue.value.map((val: any) => {
                const option = taskSubConditionKey.children.find((item) => item.value === val);
                if (option) {
                  return {
                    value: val,
                    label: option.label,
                  };
                }
                return val;
              });
            }
          }
        }
      }

      if (entityType === FILTERABLE_ENTITY_TYPES.deal) {
        conditionKey = DEAL_CONDITION_KEY;

        const productId = transformedConditionValue.value?.[0]?.value;
        if (productId && productId !== 'any') {
          referencedProducts.add(+productId);
        }
        if (Array.isArray(transformedSubConditionValue.value)) {
          transformedSubConditionValue.value.forEach((item: any) => {
            const statusId = item?.value ?? item;
            if (statusId && statusId !== 'any') {
              referencedDealStatuses.add(+statusId);
            }
          });
        }

        if (subConditionKey) {
          const dealSubConditionKey = DEAL_OPTIONS.find((option) => option.key === subConditionKey.key);
          if (dealSubConditionKey) {
            subConditionKey.label = dealSubConditionKey.value;
            subConditionKey.dataType = dealSubConditionKey.dataType;
            subConditionKey.id = dealSubConditionKey.id;
          }
        }
      }

      if (customConditionKeys.includes(prefixConditionKey)) {
        conditionKey = {
          ...conditionKey,
          conditionKeyPrefix: prefixConditionKey,
          ...CustomConditionKeyConfig[prefixConditionKey as CustomConditionKeys],
        };
      }

      return {
        id: generateId(), // Generate a unique ID for each criteria
        conditionKey,
        conditionComparator: criteria.conditionComparator,
        conditionValue: transformedConditionValue,
        type: 'criteria',
        ...(subConditionKey ? {
          subConditionKey,
          subConditionComparator: criteria.subConditionComparator,
          subConditionValue: transformedSubConditionValue,
        } : {}),
        ...(timeFilter ? ({
          timeFilter: {
            filterType: timeFilter?.filterName,
            fromTime: timeFilter?.fromTime,
            toTime: timeFilter?.toTime,
            offsetValue: timeFilter?.offsetValue,
          },
        }) : {}),
      };
    };

    // Function to process a parent node and its children
    const processParentNode = (parentNode: any, level: number) => {
      // Use optional chaining and nullish coalescing to handle possible undefined
      const parentId = parentNode?.id ? String(parentNode.id) : null;
      const children = parentId ? (childrenMap.get(parentId) || []) : [];
      const leafCriteria: any[] = [];

      // Process direct children
      children.forEach((child: any) => {
        if (child.conditionKey) {
          // This is a leaf node with criteria
          const transformedCriteria = transformCriteria(child);
          if (transformedCriteria) {
            leafCriteria.push(transformedCriteria);
          }
        } else {
          // This is another parent node, process it recursively
          processParentNode(child, level + 1);
        }
      });

      // If we have leaf criteria, create a bucket for them
      if (leafCriteria.length > 0) {
        // Use the operator from the parent node if available, otherwise default to 'AND'
        const operator = (parentNode?.operator || 'AND').toUpperCase() as BaseOperatorType;

        transformedBuckets.push({
          type: 'level',
          level,
          operator, // Use the operator from the parent node
          criteria: leafCriteria,
        });
      }
    };

    // Process each root node
    rootNodes.forEach((rootNode: any, index: number) => {
      processParentNode(rootNode, index);
    });
  });

  // Always return in the format with buckets and referencedData
  return {
    buckets: transformedBuckets,
    comparator: comparator as BaseOperatorType,
    referencedData: {
      leadAttributes: Array.from(referencedLeadAttributes),
      users: Array.from(referencedUsers),
      activities: Array.from(referencedActivities),
      salesGroups: Array.from(referencedSalesGroups),
      tasks: Array.from(referencedTasks),
      leadAttributeOptions: Array.from(referencedLeadAttributeOptions),
      products: Array.from(referencedProducts),
      dealStatuses: Array.from(referencedDealStatuses),
    },
  };
};

export const modifiedSearchFilter = (
  appliedFilters: { levels: FilterLevel[]; comparator: BaseOperatorType },
  secondaryFilters: any,
  searchFilters: any,
) => {
  const levels: FilterLevel[] = [];
  const mainLevel: FilterLevel = {
    type: 'level',
    level: 0,
    operator: 'OR' as BaseOperatorType,
    criteria: [] as CriteriaItem[],
  };

  // Handle both old and new formats
  if ('levels' in appliedFilters) {
    // New format with levels
    if (appliedFilters.levels) {
      mainLevel.operator = appliedFilters.comparator;
      // Convert each level to a nested level in criteria and transform the criteria
      appliedFilters.levels.forEach((level) => {
        // Transform all criteria items
        const transformedCriteria = transformOutgoingFilterData(level.criteria);

        if (transformedCriteria.length) {
          mainLevel.criteria.push({
            type: 'level',
            level: level.level,
            operator: level.operator,
            criteria: transformedCriteria,
          });
        }
      });
      levels.push(mainLevel);
    }
  }

  const secondaryCriteria = transformOutgoingFilterData(secondaryFilters);
  if (secondaryCriteria.length) {
    levels.push({
      type: 'level',
      level: levels.length,
      operator: 'AND',
      criteria: secondaryCriteria,
    });
  }

  const searchFilterLevel = transformOutgoingFilterData(searchFilters);
  if (searchFilterLevel.length) {
    levels.push({
      type: 'level',
      level: levels.length,
      operator: 'OR',
      criteria: searchFilterLevel,
    });
  }

  if (levels.length) {
    return {
      operator: 'AND',
      levels,
    };
  }

  return {};
};

export const updateFilterConditionsBasedOnConfigDetails = (
  configData: ConfigDetailsState,
  filtersOrLevels: CriteriaItem[] | FilterLevel[],
) => {
  const clonedData = structuredClone(filtersOrLevels || []);
  const {
    leadAttributes, activities, salesGroups, tasks, products, dealStatuses,
  } = configData;

  // Check if we're dealing with levels or criteria items
  const isLevelsArray = clonedData.length > 0
    && clonedData[0]
    && 'level' in clonedData[0]
    && !('conditionKey' in clonedData[0]);

  if (isLevelsArray) {
    // Handle levels array
    (clonedData as FilterLevel[]).forEach((level) => {
      if (level.criteria && Array.isArray(level.criteria)) {
        level.criteria = updateFilterConditionsBasedOnConfigDetails(configData, level.criteria) as CriteriaItem[];
      }
    });
    return clonedData;
  }

  // Handle criteria items
  (clonedData as CriteriaItem[]).forEach((filter) => {
    if (filter.type === 'level') {
      filter.criteria = updateFilterConditionsBasedOnConfigDetails(configData, filter.criteria) as CriteriaItem[];
      return;
    }

    const {
      conditionKey, conditionValue, subConditionKey, subConditionValue,
    } = filter;

    if (!conditionKey) return;

    const { schemaName, entityType } = conditionKey;
    const attribute = schemaName && leadAttributes[schemaName];
    if (attribute) {
      conditionKey.label = attribute.attributeName;
      conditionKey.dataType = attribute.dataType as DataTypeType;
    }

    if (entityType === FILTERABLE_ENTITY_TYPES.salesGroup) {
      if (conditionValue && Array.isArray(conditionValue.value)) {
        conditionValue.value = conditionValue.value.map((val: any) => {
          const salesGroup = salesGroups.find((sg) => sg.id === val.value);
          if (salesGroup) {
            return {
              value: val.value,
              label: salesGroup?.name,
            };
          }
          return val;
        });
      }
    }

    if (entityType === FILTERABLE_ENTITY_TYPES.taskType) {
      if (conditionValue && Array.isArray(conditionValue.value)) {
        conditionValue.value = conditionValue.value.map((val: any) => {
          const task = tasks.find((t) => t.id === val.value);
          if (task) {
            return {
              value: val.value,
              label: task?.name,
            };
          }
          return val;
        });
      }
    }

    if (entityType === FILTERABLE_ENTITY_TYPES.deal) {
      if (conditionValue && Array.isArray(conditionValue.value)) {
        conditionValue.value = conditionValue.value.map((val: any) => {
          const product = products.find((p) => String(p.id) === String(val.value));
          return product ? { value: val.value, label: product.name } : val;
        });
      }
      if (subConditionValue && Array.isArray(subConditionValue.value)) {
        subConditionValue.value = subConditionValue.value.map((val: any) => {
          const dealStatus = dealStatuses.find((d) => String(d.id) === String(val.value));
          return dealStatus ? { value: val.value, label: dealStatus.name } : val;
        });
      }
    }

    if (entityType === FILTERABLE_ENTITY_TYPES.leadActivity) {
      const activityId = conditionValue?.value?.[0]?.value;
      const activity = activityId && activities[activityId];
      if (activity && conditionValue && Array.isArray(conditionValue.value) && conditionValue.value.length > 0) {
        conditionValue.value[0].label = activity.value;
        if (subConditionKey) {
          const activityAttribute = activity.activityAttributes.find(
            (attr: { key: string }) => attr.key === subConditionKey.key,
          );
          if (activityAttribute) {
            subConditionKey.dataType = activityAttribute.dataType || 'default';
            subConditionKey.label = activityAttribute.value;
            subConditionKey.value = activityAttribute.key;
            subConditionKey.id = activityAttribute.id;

            if (Array.isArray(subConditionValue?.value)) {
              if (subConditionKey.dataType !== ALL_DATA_TYPES.reference_user) {
                subConditionValue.value = subConditionValue.value?.map((val) => {
                  const option = activityAttribute.activityAttributeOptions?.find(
                    (item: any) => item.key === val.value,
                  );
                  return {
                    key: val.value,
                    value: val.value,
                    label: option?.value || val.value,
                  };
                });
              }
            }
          }
        }
      }
    }
  });

  return clonedData;
};

export const getReferencedDataFromAppliedFilters = (filters: CriteriaItem[]) => {
  // Initialize an empty array to store transformed criteria
  const referencedLeadAttributes = new Set<string>();
  const referencedUsers = new Set<string>();
  const referencedActivities = new Set<number>();
  const referencedSalesGroups = new Set<number>();
  const referencedTasks = new Set<number>();
  const referencedLeadAttributeOptions = new Set<number>();

  // Iterate through the backend data's criteria levels
  filters.forEach((item: CriteriaItem) => {
    if (item.type === 'level') {
      const nestedData = getReferencedDataFromAppliedFilters(item.criteria);
      nestedData.referencedData.leadAttributes.forEach((attr) => referencedLeadAttributes.add(attr));
      nestedData.referencedData.users.forEach((user) => referencedUsers.add(user));
      nestedData.referencedData.activities.forEach((activity) => referencedActivities.add(activity));
      nestedData.referencedData.salesGroups.forEach((group) => referencedSalesGroups.add(group));
      nestedData.referencedData.tasks.forEach((task) => referencedTasks.add(task));
      nestedData.referencedData.leadAttributeOptions.forEach((option) => referencedLeadAttributeOptions.add(option));
      return;
    }

    const {
      conditionKey, conditionValue, subConditionKey, subConditionValue,
    } = item;
    // Create a transformed criteria object based on the configuration map
    const { entityType } = conditionKey;
    if (conditionKey.entityType === FILTERABLE_ENTITY_TYPES.leadAttribute) {
      if (conditionKey.dataType === ALL_DATA_TYPES.reference_user) {
        (conditionValue?.value as { value: string }[]).forEach((val) => referencedUsers.add(val.value));
      } else {
        referencedLeadAttributes.add(conditionKey.schemaName);
      }

      if (conditionValue?.value && Array.isArray(conditionValue?.value)) {
        conditionValue.value?.forEach(
          (option: { value: string }) => referencedLeadAttributeOptions.add(parseInt(option?.value, 10)),
        );
      }
    }

    if (entityType === FILTERABLE_ENTITY_TYPES.leadActivity) {
      const activityId = conditionValue?.value[0]?.value;
      if (activityId !== 'any') {
        referencedActivities.add(+activityId);
      }
      if (subConditionKey?.dataType === ALL_DATA_TYPES.reference_user) {
        (subConditionValue?.value as { value: string }[]).forEach((subVal) => referencedUsers.add(subVal.value));
      }
    }

    if (entityType === FILTERABLE_ENTITY_TYPES.salesGroup) {
      if (conditionValue && Array.isArray(conditionValue?.value)) {
        (conditionValue.value).forEach((val) => referencedSalesGroups.add(+val.value));
      }
    }

    if (entityType === FILTERABLE_ENTITY_TYPES.taskType) {
      const taskId = conditionValue?.value[0]?.value;
      if (taskId !== 'any') {
        referencedTasks.add(+taskId);
      }

      if (subConditionKey?.dataType === ALL_DATA_TYPES.reference_user) {
        (subConditionValue?.value as { value: string }[]).forEach((subVal) => referencedUsers.add(subVal.value));
      }
    }
  });

  // Return the referenced data
  return {
    referencedData: {
      leadAttributes: Array.from(referencedLeadAttributes),
      users: Array.from(referencedUsers),
      activities: Array.from(referencedActivities),
      salesGroups: Array.from(referencedSalesGroups),
      tasks: Array.from(referencedTasks),
      leadAttributeOptions: Array.from(referencedLeadAttributeOptions),
    },
  };
};
